import { shouldShowAddressFieldForTaxCalculation, shouldShowZipCodeFieldForTaxCalculation } from '@/utils/tax';
import { Address } from '../Address/Address';
import { ZipCode } from '../ZipCode/ZipCode';
import { CheckoutContext } from '@/context/CheckoutContext';
import { useContext, useState, useEffect, useCallback } from 'react';
import type { Address as AddressType } from '@/api/address';

interface InputTaxComponentProps {
  onValidate?: (isValid: boolean) => void;
  onZipCodeValidate?: (isValid: boolean) => void;
  onAddressSelect?: (address: AddressType) => void;
  isBlockedForZipCode?: boolean;
}

export const InputTaxComponent = ({
  onValidate,
  onZipCodeValidate,
  onAddressSelect,
  isBlockedForZipCode = false,
}: InputTaxComponentProps) => {
  const {
    customer,
    featureFlags,
    actions: { setIsFormValid },
  } = useContext(CheckoutContext);
  const [addressValid, setAddressValid] = useState(false);
  const [zipCodeValid, setZipCodeValid] = useState(false);

  const shouldShowAddress = shouldShowAddressFieldForTaxCalculation(customer, featureFlags);
  const shouldShowZipCode = shouldShowZipCodeFieldForTaxCalculation(customer);

  useEffect(() => {
    let isValid = true;

    if (shouldShowAddress) {
      isValid = isValid && addressValid;
    }

    if (shouldShowZipCode) {
      isValid = isValid && zipCodeValid;
    }

    if (!shouldShowAddress && !shouldShowZipCode) {
      isValid = true;
    }

    setIsFormValid(isValid);
    if (onValidate) {
      onValidate(isValid);
    }
  }, [shouldShowAddress, shouldShowZipCode, addressValid, zipCodeValid, setIsFormValid, onValidate]);

  const handleAddressValidate = useCallback((isValid: boolean) => {
    setAddressValid(isValid);
  }, []);

  const handleZipCodeValidate = useCallback(
    (isValid: boolean) => {
      setZipCodeValid(isValid);
      // Also call the separate zip code validation callback if provided
      if (onZipCodeValidate) {
        onZipCodeValidate(isValid);
      }
    },
    [onZipCodeValidate]
  );

  return (
    <>
      {shouldShowAddress && <Address onValidate={handleAddressValidate} onSelect={onAddressSelect} />}
      {shouldShowZipCode && <ZipCode onValidate={handleZipCodeValidate} isBlockedForZipCode={isBlockedForZipCode} />}
    </>
  );
};
